import React, { useCallback, useEffect, useState } from 'react'
import { CircularProgress, SelectChangeEvent } from '@mui/material'
import useCreateContent from '../services/hooks/useCreateContent'
import useGetContent from '../services/hooks/useGetContent'
import useUpdateContent from '../services/hooks/useUpdateContent'
import Loader from '../../../components/Loader'
import FormSelect from '../../../components/Common/FormSelect'
import CustomEditor from '../../../components/Common/EditorInput'
import StyledButton from '../../../components/Button/Button'
import { TermsType } from '../../../utils/config/auth-enums'

type EditorType = {
  getData: () => string
}

const EsignAgreement = () => {
  const [termsrole, setTermsRole] = useState<string>(TermsType.PARTICIPANT)
  const [contentId, setContentId] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  // const [isEditEnabled, setIsEditEnabled] = useState<string | null>(null)
  const [editorData, setEditorData] = useState('')

  const { mutate: createContent, isPending } = useCreateContent()
  const { mutate: getContent, isPending: isContentPending } = useGetContent()
  const { mutate: updateContent, isPending: updatePending } = useUpdateContent()

  const options = [
    { value: TermsType.PARTICIPANT, label: 'Participant' },
    { value: TermsType.SUPERVISOR, label: 'Supervisor' },
  ]

  const fetchContent = useCallback(
    (termsrole: string) => {
      getContent(
        { type: 'ESIGN_AGREEMENT', role: termsrole },
        {
          onSuccess: (response) => {
            if (response?.data?.content.length > 1) {
              setIsEdit(true)
              setContentId(response?.data?.content[0]._id)
              setEditorData(response.data.content[0].description)
            }
          },
          onError: (error) => {
            console.log(error)
          },
        },
      )
    },
    [getContent],
  )

  const isFormValid = () => {
    return editorData !== ''
  }

  const handleRoleChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string
    setTermsRole(value as TermsType)
  }

  const handleEditorChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    editor: EditorType,
  ) => {
    const data = editor.getData()
    setEditorData(data)
  }

  const handleSubmit = () => {
    if (isEdit && contentId) {
      updateContent(
        {
          contentId: contentId,
          role: termsrole,
          description: editorData,
        },
        {
          onSuccess: () => {
            fetchContent(termsrole)
          },
        },
      )
    } else {
      createContent(
        {
          type: 'ESIGN_AGREEMENT',
          role: termsrole,
          description: editorData,
        },
        {
          onSuccess: () => {
            fetchContent(termsrole)
          },
        },
      )
    }
  }

  useEffect(() => {
    fetchContent(termsrole)
  }, [fetchContent, termsrole])

  if (isContentPending) {
    return <Loader />
  }

  return (
    <form>
      <div className="mt-6 w-full max-w-[886px] flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <FormSelect
            value={termsrole}
            onChange={handleRoleChange}
            label="Select User Type"
            name="userRole"
            options={options}
            className="px-10px"
          />
          <div className="no-tailwind">
            <CustomEditor data={editorData} onChange={handleEditorChange} />
          </div>
        </div>
        <StyledButton
          className={`h-[63px] w-[117px] btn-policy ${
            !isFormValid()
              ? 'cursor-not-allowed bg-gray-200 text-gray-300 border border-gray-400'
              : ''
          }`}
          disabled={!isFormValid()}
          onClick={handleSubmit}
        >
          {isPending || updatePending ? (
            <CircularProgress className="!text-[#181a21] !text-2xl" />
          ) : (
            'Save'
          )}
        </StyledButton>
      </div>
    </form>
  )
}

export default EsignAgreement
